import { useEffect, useMemo } from "react";

import { ChevronDown, ChevronUp, Loader2, X } from "lucide-react";
import { Link } from "react-router";

import { SCENARIO_ASSIGNMENT_STATUS_STYLES } from "@/components/pages_logged_in/roleplays/utils";
import { Card } from "@/components/ui/card";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import {
  RoleplayRank,
  ScenarioAssignmentDisplayStatus,
  useScenarioAssignmentDetailQuery,
} from "@/graphql";
import { formatDate } from "@/utils/datetime";

import { SimpleScenarioCard } from "./AssignScenarioDialog";
import { SessionHistoryChartSection } from "./SessionHistoryChartSection";
import { SessionHistoryListSection } from "./SessionHistoryListSection";

interface AssignmentResultDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleplaySlug?: string;
  assignmentId: string;
  onPrevClick: () => void;
  onNextClick: () => void;
}

export function AssignmentResultDrawer({
  open,
  onOpenChange,
  roleplaySlug,
  assignmentId,
  onPrevClick,
  onNextClick,
}: AssignmentResultDrawerProps) {
  // Fetch detailed assignment data including sessions
  const {
    data: assignmentDetailData,
    loading: detailLoading,
    error: detailError,
    refetch,
  } = useScenarioAssignmentDetailQuery({
    variables: {
      assignmentId,
    },
    skip: !open,
  });

  useEffect(() => {
    if (assignmentId) {
      refetch();
    }
  }, [assignmentId]);

  // Process data for display
  const assignmentDetail = assignmentDetailData?.scenarioAssignmentDetail;
  const assignment = assignmentDetail?.assignment;
  const sessions = assignmentDetail?.completionSessions || [];

  const content = useMemo(() => {
    if (detailLoading) {
      return (
        <div className="flex items-center justify-center h-64 gap-2 flex-col">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading assignment details...</span>
        </div>
      );
    }

    if (detailError || !assignment || !assignmentDetail) {
      return (
        <div className="flex items-center justify-center h-64">
          <div>Error loading assignment details</div>
        </div>
      );
    }
    const config =
      SCENARIO_ASSIGNMENT_STATUS_STYLES[
        assignment?.displayStatus || ScenarioAssignmentDisplayStatus.NotStarted
      ];

    return (
      <div className="flex flex-col h-full">
        {/* Header with close button and controls */}

        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Participant and Menu */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-gray-200 rounded-full overflow-hidden">
                {assignment.assignee?.photo && (
                  <img
                    src={assignment.assignee.photo}
                    alt={assignment.assignee.name || ""}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
              </div>
              <span className="font-semibold text-xl text-[#242424]">
                {assignment.assignee?.name || "Unknown User"}
              </span>
            </div>
          </div>

          {/* Assignment Details */}
          <Card className="p-6 border border-[#E2E2E2] bg-white">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[#778397] w-28">Assigned by:</span>
                <span className="text-[#242424] font-medium">
                  {assignment.createdBy?.name || "Unknown"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#778397] w-28">Assigned on:</span>
                <span className="text-[#242424] font-medium">
                  {formatDate(assignment.createdAt, "MMM d, yyyy") || "Unknown"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#778397] w-28">Completed:</span>
                <span className="text-[#242424] font-medium">
                  {assignment.completedAt
                    ? formatDate(assignment.completedAt, "MMM d, yyyy")
                    : "-"}
                </span>
              </div>
              {assignment.dueDate && (
                <div className="flex justify-between items-center">
                  <span className="text-[#778397] w-28">Due Date:</span>
                  <span className="text-[#242424] font-medium">
                    {formatDate(assignment.dueDate, "MMM d, yyyy") || "Unknown"}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-[#778397] w-28">Status:</span>
                <div
                  className="flex items-center px-2 py-1 rounded-sm gap-2 w-fit"
                  style={{ backgroundColor: config.backgroundColor }}
                >
                  {config.icon}
                  <div style={{ color: config.textColor }}>{config.text}</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Scenario Section */}
          <div className="space-y-3">
            <h3 className="font-semibold text-base text-black">Scenario</h3>
            {assignment.scenario && (
              <Link
                to={`/scenarios/${assignment.scenario?.slug}`}
                className="block transition-colors duration-200 text-gray-900 hover:text-inherit"
              >
                <SimpleScenarioCard
                  scenario={assignment.scenario}
                  selected={false}
                  onToggle={() => {}}
                  selectable={false}
                  avatarSize={100}
                />
              </Link>
            )}
          </div>

          {/* Progression Section */}
          <SessionHistoryChartSection
            sessions={sessions.map((session) => ({
              uuid: session?.uuid || "",
              updatedAt: session?.updatedAt || "",
              rank: (session?.rank as RoleplayRank) || RoleplayRank.Unranked,
              score: session?.score || 0,
              assigneeName: assignment.assignee?.name || "",
              roleplaySlug: roleplaySlug || "",
            }))}
            scenarioSlug={assignment.scenario?.slug || ""}
          />

          {/* History Section */}
          <SessionHistoryListSection
            sessions={sessions.map((session) => ({
              uuid: session?.uuid || "",
              updatedAt: session?.updatedAt || "",
              rank: (session?.rank as RoleplayRank) || RoleplayRank.Unranked,
              score: session?.score || 0,
            }))}
            scenarioSlug={assignment.scenario?.slug || ""}
            assigneeName={assignment.assignee?.name || ""}
          />
        </div>
      </div>
    );
  }, [assignment, detailLoading, detailError, assignmentDetail, sessions]);

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className="max-w-lg!">
        <div className="flex items-center justify-between p-3 absolute top-6 -left-12 rounded-md bg-white rounded-r-none">
          <div className="flex flex-col items-center gap-2">
            <div
              className="w-6 h-6 flex items-center justify-center cursor-pointer"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              <X className="w-5 h-5" />
            </div>
            <div className="w-6 h-px bg-gray-300"></div>
            <div
              className="w-6 h-6  flex items-center justify-center cursor-pointer"
              onClick={onPrevClick}
            >
              <ChevronUp className="w-4 h-4" />
            </div>
            <div
              className="w-6 h-6 flex items-center justify-center cursor-pointer"
              onClick={onNextClick}
            >
              <ChevronDown className="w-4 h-4" />
            </div>
          </div>
        </div>
        {content}
      </DrawerContent>
    </Drawer>
  );
}
